import { useCallback } from 'react';
import { SelectedCurrentConditionType } from 'types/searchCondition';
import { moveToSearchResultPage } from '../logics/moveToSearchResultPage';

/**
 * 検索処理を行うカスタムフック
 *
 * @param {SelectedCurrentConditionType} featuredSelectedSearchCondition - 選択された検索条件
 * @param {string | undefined} fidQuery - フィーチャーIDクエリ
 * @returns {Object} - 検索ボタンのクリックハンドラ
 */
export const useSearch = (
  featuredSelectedSearchCondition: SelectedCurrentConditionType,
  fidQuery: string | undefined
) => {
  /**
   * サイドバー上での検索処理
   */
  const onClickSearchButton = useCallback(() => {
    moveToSearchResultPage({
      featuredSelectedSearchCondition,
      fidQuery,
    });
  }, [fidQuery, featuredSelectedSearchCondition]);

  /**
   * 職種モーダルでの検索処理
   *
   * @param {Object} selectedOccupation - 選択された職種情報
   * @param {number} selectedOccupation.searchJobTypeLId - 大分類職種ID
   * @param {number} selectedOccupation.searchJobTypeMId - 中分類職種ID
   * @param {number} selectedOccupation.searchJobTypeSId - 小分類職種ID
   */
  const onClickOccupationModalSearchButton = useCallback(
    (selectedOccupation: {
      searchJobTypeLId: SelectedCurrentConditionType['searchJobTypeLId'];
      searchJobTypeMId: SelectedCurrentConditionType['searchJobTypeMId'];
      searchJobTypeSId: SelectedCurrentConditionType['searchJobTypeSId'];
    }) => {
      moveToSearchResultPage({
        featuredSelectedSearchCondition: {
          ...featuredSelectedSearchCondition,
          ...selectedOccupation,
        },
        fidQuery,
      });
    },
    [fidQuery, featuredSelectedSearchCondition]
  );

  /**
   * 勤務地モーダルでの検索処理
   *
   * @param {Object} selectedLocation - 選択された勤務地情報
   * @param {number} selectedLocation.areaId - 地域ID
   * @param {number} selectedLocation.prefectureId - 都道府県ID
   * @param {number} selectedLocation.cityId - 市区町村ID
   * @param {number} selectedLocation.wardId - 区ID
   */
  const onClickLocationModalSearchButton = useCallback(
    (selectedLocation: {
      areaId: SelectedCurrentConditionType['areaId'];
      prefectureId: SelectedCurrentConditionType['prefectureId'];
      cityId: SelectedCurrentConditionType['cityId'];
      wardId: SelectedCurrentConditionType['wardId'];
    }) => {
      moveToSearchResultPage({
        featuredSelectedSearchCondition: {
          ...featuredSelectedSearchCondition,
          ...selectedLocation,
        },
        fidQuery,
      });
    },
    [fidQuery, featuredSelectedSearchCondition]
  );

  /**
   * 路線駅モーダルでの検索処理
   *
   * @param {Object} selectedRouteStation - 選択された路線駅情報
   * @param {number} selectedRouteStation.routeId - 路線ID
   * @param {number} selectedRouteStation.stationId - 駅ID
   */
  const onClickRoteStationModalSearchButton = useCallback(
    (selectedRouteStation: {
      routeId: SelectedCurrentConditionType['routeId'];
      stationId: SelectedCurrentConditionType['stationId'];
    }) => {
      moveToSearchResultPage({
        featuredSelectedSearchCondition: {
          ...featuredSelectedSearchCondition,
          ...selectedRouteStation,
        },
        fidQuery,
      });
    },
    [fidQuery, featuredSelectedSearchCondition]
  );

  return {
    onClickSearchButton,
    onClickOccupationModalSearchButton,
    onClickLocationModalSearchButton,
    onClickRoteStationModalSearchButton,
  };
};
